import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { count } from '../lib/format.js';
import { ChipBar, FilterChip, FilterPills, Menu, MenuItem, Screen, inputStyle } from '../components/Screen.js';
import { EventDetail } from './EventDetail.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Identity,
  Note,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';

const PAGE = 50;

/**
 * What is happening across the platform.
 *
 * ── A different question from the per-account view ───────────────────
 *
 * The activity panel on a profile answers "what happened to this account", which
 * requires you to already suspect an account. This answers "what is happening",
 * which is the question actually asked when something is wrong and nobody knows
 * where yet: failed sign-ins spread across many accounts, an admin acting at an
 * odd hour, a spike in bans.
 *
 * ── Why the type filter is a click, not a dropdown ───────────────────
 *
 * A dropdown needs the list of event types, and the portal has no way to get it
 * — so it would carry a hardcoded copy that drifts silently as types are added
 * in the auth service. Every row already shows its type; clicking one filters to
 * it. Nothing to keep in step, and it is the faster interaction anyway.
 *
 * ── Paging rather than infinite scroll ───────────────────────────────
 *
 * The old portal scrolled infinitely and capped at 1,000 with a banner. Explicit
 * pages are better on this screen for one reason: an admin reading a feed during
 * an incident needs to be able to say WHERE they are, and "somewhere past the
 * fourth scroll" is not a position anybody can return to or describe to somebody
 * else. The count line names the offset.
 */
const PRESETS = [
  { value: '1', label: 'Last hour' },
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '0', label: 'All time' },
] as const;

export function Activity({
  eventId,
  userId,
  navigate,
}: {
  eventId?: string;
  userId?: string;
  navigate: (to: string) => void;
}) {
  const [hours, setHours] = useState<string>('24');
  const [type, setType] = useState<string>();
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [offset, setOffset] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState<string | undefined>(userId);
  const search = useChips([], () => setOffset(0));

  /**
   * The window is a timestamp computed when the preset changes, not on every
   * render. A `since` that moves with the clock would make the fetch key change
   * constantly and refetch forever.
   *
   * Explicit From/To wins over the preset when set — they are the same filter
   * expressed two ways, and a preset that silently overrode a typed date would
   * be a control that ignores its own input.
   */
  const [presetSince, setPresetSince] = useState<string | undefined>(() => isoAgo(24));

  const since = from ? new Date(from).toISOString() : presetSince;
  const until = to ? new Date(to).toISOString() : undefined;
  const usingRange = Boolean(from || to);

  const applyPreset = (value: string) => {
    setHours(value);
    setPresetSince(isoAgo(Number(value)));
    setFrom('');
    setTo('');
    setOffset(0);
  };

  const { state, reload } = useAsync(
    () =>
      adminApi.activityFeed({
        type,
        since,
        until,
        sort,
        userId: subject,
        terms: search.active,
        limit: PAGE,
        offset,
      }),
    [search.key, type, since, until, sort, subject, offset],
  );

  /**
   * THE FEED'S STATE OUTLIVES THE DRILL-IN, and that is why this screen is
   * shaped differently from Users, Applications and Agreements.
   *
   * Those three are thin routers: the list unmounts when a record opens, and its
   * filter resets on the way back. That is a fair trade there — a status pill
   * and a page number are cheap to redo, and it is the shape the Security groups
   * screen already shipped with.
   *
   * It is not a fair trade here. This screen's state is a time window, a typed
   * date range, a sort order, a type filter, a page and a set of search chips —
   * assembled during an incident, by somebody who is about to open an event
   * BECAUSE of what those filters surfaced. Resetting all of it every time they
   * read a row would make the drill-in cost more than it gives, which is what
   * the modal was quietly getting right.
   *
   * So the hooks above run either way and only the BODY changes. Nothing is
   * unmounted, so nothing is lost. The feed also keeps loading behind the event,
   * which is not laziness — it means coming back shows rows rather than a
   * loading note.
   *
   * `backTo` carries the `?user=` parameter because App keys this screen on it:
   * a back link that dropped it would remount the very thing this is protecting.
   */
  if (eventId) {
    return <EventDetail key={eventId} id={eventId} backTo={feedPath(userId)} />;
  }

  return (
    <Screen
      filters={
        <>
          <FilterPills
            label="Time window"
            current={usingRange ? '' : hours}
            onSelect={applyPreset}
            options={PRESETS.map((p) => ({ value: p.value as string, label: p.label }))}
          />
          <FilterPills
            label="Sort order"
            current={sort}
            onSelect={(v) => {
              setSort(v);
              setOffset(0);
            }}
            options={[
              { value: 'newest' as const, label: 'Newest' },
              { value: 'oldest' as const, label: 'Oldest' },
            ]}
          />
        </>
      }
      actions={
        <>
          <label style={dateLabel}>
            From
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setOffset(0);
              }}
              style={{ ...inputStyle, width: 190 }}
            />
          </label>
          <label style={dateLabel}>
            To
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setOffset(0);
              }}
              style={{ ...inputStyle, width: 190 }}
            />
          </label>
          {usingRange ? (
            <Button
              onClick={() => {
                setFrom('');
                setTo('');
                applyPreset(hours);
              }}
            >
              Clear dates
            </Button>
          ) : null}
          <Button
            onClick={() => {
              // Re-anchoring the window is the point of refreshing a feed scoped
              // to "the last hour". Reloading without it would return the same
              // hour, growing staler each time.
              if (!usingRange) setPresetSince(isoAgo(Number(hours)));
              reload();
            }}
          >
            Refresh
          </Button>
        </>
      }
      chips={
        <>
          <ChipBar
            search={search}
            placeholder="Search event type, account, actor or IP…"
            appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
          />
          {/* The filters that are not chips, rendered as removable ones anyway —
              a screen opened pre-filtered from a profile must SAY it is filtered,
              or an empty feed reads as "nothing happened". */}
          {type || subject ? (
            <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
              {type ? (
                <FilterChip label={`type: ${type}`} onRemove={() => setType(undefined)} />
              ) : null}
              {subject ? (
                <FilterChip label="one account" onRemove={() => setSubject(undefined)} />
              ) : null}
            </div>
          ) : null}
        </>
      }
    >
      <Panel title="Platform activity">
        <AsyncBoundary state={state}>
          {(d) => (
            <>
              {d.events.length === 0 ? (
                <EmptyState
                  title="Nothing recorded in this window"
                  suggestion={
                    <>
                      This is the server reporting no rows, not a failed request. Try a wider window,
                      or remove a filter.
                    </>
                  }
                />
              ) : (
                <Table columns={['When', 'Event', 'Account', 'Actor', 'IP', '']}>
                  {d.events.map((e) => (
                    /**
                     * Double-click opens it, which is what the old admin panel
                     * did and what anyone who used that will try first. It is
                     * deliberately NOT the only way in — a double-click is
                     * invisible to everyone else, and the two buttons inside
                     * this row already spend the single click on filtering. The
                     * ellipsis menu in the last column is the discoverable half.
                     */
                    <tr
                      key={e.id}
                      className="rw-rowhover"
                      onDoubleClick={(ev) => {
                        /**
                         * Not when the double-click landed on a control.
                         *
                         * Two of this row's cells are filter buttons and the
                         * last holds the menu, so without this a double-click on
                         * "filter to this type" would filter AND open the modal
                         * — two different things from one gesture, which reads
                         * as a bug even when both were wanted.
                         *
                         * `closest('button')` rather than a list of refs so it
                         * keeps holding for whatever is added to this row next.
                         */
                        if ((ev.target as HTMLElement).closest('button')) return;
                        navigate(eventPath(e.id, userId));
                      }}
                    >
                      <Td>
                        <When iso={e.createdAt} />
                      </Td>
                      <Td>
                        <button
                          type="button"
                          onClick={() => {
                            setType(e.type);
                            setOffset(0);
                          }}
                          title={`Filter to ${e.type}`}
                          style={{ ...typeButton, color: e.type === type ? C.brand : C.text }}
                        >
                          {e.type}
                        </button>
                      </Td>
                      <Td>
                        <button
                          type="button"
                          onClick={() => {
                            setSubject(e.subject.id);
                            setOffset(0);
                          }}
                          title="Filter to this account"
                          style={{ ...typeButton, fontFamily: 'inherit', fontSize: 'inherit' }}
                        >
                          <Identity
                            email={e.subject.email}
                            name={e.subject.name}
                            pseudonymized={e.subject.pseudonymized}
                          />
                        </button>
                      </Td>
                      <Td>
                        {e.actor ? (
                          <Identity email={e.actor.email} />
                        ) : (
                          /* No actor means the subject acted for themselves, or
                             the actor's account has since been purged.
                             `actorType` is what distinguishes them. */
                          <Badge>{e.actorType}</Badge>
                        )}
                      </Td>
                      <Td mono>{e.ipAddress ?? '—'}</Td>
                      <Td>
                        <span style={{ display: 'inline-flex', gap: '.4rem', alignItems: 'center' }}>
                          <Menu label={`Actions for ${e.type} on ${e.subject.email ?? e.subject.id}`}>
                            {(close) => (
                              <>
                                <MenuItem href={`#${eventPath(e.id, userId)}`} onClick={close}>
                                  Show details
                                </MenuItem>
                                <MenuItem
                                  href={`#/users/${encodeURIComponent(e.subject.id)}`}
                                  onClick={close}
                                >
                                  View account
                                </MenuItem>
                                {/* Only when there is one. An event with no
                                    actor was done by the subject or the system,
                                    and a menu item that goes nowhere is worse
                                    than an absent one. */}
                                {e.actor ? (
                                  <MenuItem
                                    href={`#/users/${encodeURIComponent(e.actor.id)}`}
                                    onClick={close}
                                  >
                                    View actor
                                  </MenuItem>
                                ) : null}
                              </>
                            )}
                          </Menu>
                          {/*
                            Collapsed repeats. One row saying ×47 is the honest
                            rendering of 47 attempts inside the window.

                            AFTER the menu, which is not reading order and is
                            deliberate (Jon, 2026-08-29). This cell is
                            left-aligned, so a badge in front of the menu pushes
                            it right on exactly the rows that have one — the ⋯
                            sat about 55px off the column on a `×2` row while
                            every other row lined up. Trailing it, the menu
                            starts at the same x on every row and the badge
                            extends into the empty space beyond.

                            Do NOT "fix" this by adopting `actionsLast` /
                            `<Td action>` like the other tables here. Those
                            right-align the cell, which realigns the ⋯ only if
                            the badge comes FIRST — the arrangement this
                            replaced.
                          */}
                          {e.count > 1 ? <Badge tone="warn">×{e.count}</Badge> : null}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}

              <div style={pager}>
                <span style={{ fontSize: '.82rem', color: C.muted }}>
                  {count(d.total === 0 ? 0 : offset + 1)}–{count(offset + d.events.length)} of{' '}
                  {count(d.total)} in this window, {d.sort} first
                </span>
                <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
                  <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                    Previous
                  </Button>
                  <Button
                    disabled={offset + d.events.length >= d.total}
                    onClick={() => setOffset(offset + PAGE)}
                  >
                    Next
                  </Button>
                </span>
              </div>

              {d.total > 5000 ? (
                <Note>
                  {count(d.total)} events match. Paging this far is slow at the far end. Narrow the
                  window or add a search term rather than walking to the last page.
                </Note>
              ) : null}
            </>
          )}
        </AsyncBoundary>
      </Panel>

    </Screen>
  );
}

/**
 * The feed's own address, and one event's, both carrying `?user=`.
 *
 * App keys this screen on that parameter, so every link between the feed and an
 * event has to preserve it or the round trip remounts the feed and discards the
 * window, the range, the page and the chips. Written once, here, because there
 * are four such links — the row's double-click, its menu item, the event's back
 * link — and three of them agreeing is not good enough.
 */
function feedPath(userId?: string): string {
  return userId ? `/activity?user=${encodeURIComponent(userId)}` : '/activity';
}

function eventPath(eventId: string, userId?: string): string {
  const at = `/activity/${encodeURIComponent(eventId)}`;
  return userId ? `${at}?user=${encodeURIComponent(userId)}` : at;
}

/** `undefined` for "all time" — the server treats an absent `since` as no lower
 *  bound, and one computed from zero hours would mean "now". */
function isoAgo(hours: number): string | undefined {
  if (!hours || hours <= 0) return undefined;
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

const typeButton = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  fontFamily: 'ui-monospace, monospace',
  fontSize: '.8rem',
  textAlign: 'left' as const,
  textDecoration: 'underline',
  textDecorationStyle: 'dotted' as const,
  textUnderlineOffset: 3,
};

const dateLabel = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.35rem',
  fontSize: '.79rem',
  color: C.muted,
};

const pager = {
  display: 'flex',
  alignItems: 'center',
  gap: '.75rem',
  padding: '.7rem 1.2rem',
  borderTop: `1px solid ${C.border}`,
};
